import {
  AttachmentMeta,
  MessageContentEntity,
  VoiceAttachmentMeta,
} from "@/indexedDB/types";
import {
  decryptAsset,
  decryptString,
  encryptAsset,
  encryptString,
} from "@/lib/crypto/crypto";
import { encryptChunk } from "@/lib/crypto/window-crypto";
import { isUserAuthenticated } from "./auth";
import { resolveCSK, EpochPurgedError } from "./epochKeyCache";
import { getAttachmentCategory } from "@/lib/utils";
import { blobToUint8Array, hexToUint8Array } from "@/lib/dataConversions";
import { EncryptedMessage } from "@/server/types/message";
import i18n from "@/config/i18n";
import {
  extractLinksFromHtmlWithTiptap,
  extractLinksFromText,
} from "@/lib/linkExtractor";
import { isHtmlContent } from "@/lib/markdown";
import { EncryptedMessage as UmbraEncryptedMessage } from "@/server/types/umbra/message";
import {
  DecryptionError,
  isDecryptionError,
  logDecryptionFailure,
  withDecryptionErrorHandling,
} from "./decryptionError";
import { normalizeMimeType } from "@/lib/mime";

// Re-exported for existing importers; the implementation now lives in @/lib/mime
// so low-level storage code can share it without importing middleware/crypto.
export { normalizeMimeType };

// TO DO => need uniformisation ? receive only ecsk or csk
// TO DO => apply middle cache layer for decoding secret keys

export const decryptMessageContent = async (
  chatId: string,
  epoch: number,
  eCSK: string,
  encryptedContent: string,
  encryptedAttachmentMeta?: string,
): Promise<MessageContentEntity> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    logDecryptionFailure("MISSING_AUTH");
    throw new DecryptionError("MISSING_AUTH");
  }

  // Epoch 0 is the pre-rotation baseline that carries the inline eCSK; for
  // rotated epochs (>0) the key comes from the epoch cache, so a missing eCSK
  // is only fatal at epoch 0.
  if (!eCSK && epoch === 0) {
    logDecryptionFailure("MISSING_ECSK");
    throw new DecryptionError("MISSING_ECSK");
  }

  // CSK resolution: `resolveCSK` handles the hot/warm epoch caches and the
  // inline eCSK (epoch 0). A transient failure (decapsulation, keys not yet
  // synced) is wrapped as a retryable DecryptionError so the orchestrator
  // re-attempts later. An EpochPurgedError is terminal — the epoch key is
  // gone from this device — and is allowed to propagate.
  let csk: string;
  try {
    csk = await resolveCSK(
      chatId,
      epoch,
      epoch === 0 ? eCSK || undefined : undefined,
    );
  } catch (error) {
    if (error instanceof EpochPurgedError) {
      throw error;
    }
    logDecryptionFailure("CSK_DECAPSULATION_FAILED");
    throw new DecryptionError("CSK_DECAPSULATION_FAILED");
  }

  // Ciphertext: a failure here is the message itself, full stop.
  const decryptedContent = await withDecryptionErrorHandling(
    "CIPHERTEXT_DECRYPT_FAILED",
    async () =>
      encryptedContent ? await decryptString(encryptedContent, csk) : "",
  );

  // Partial success: the text portion has succeeded above. If decrypting the
  // attachment metadata then throws, return the text and flag the attachment
  // so the UI can show a "broken attachment" state without losing the
  // message content.
  let decryptedAttachmentMeta: Omit<MessageContentEntity, "text" | "link">;
  let attachmentMetaFailed = false;
  try {
    decryptedAttachmentMeta = await decryptMessageAttachmentMeta(
      encryptedAttachmentMeta || "",
      csk,
    );
  } catch (error) {
    if (isDecryptionError(error)) {
      attachmentMetaFailed = true;
      decryptedAttachmentMeta = {
        media: undefined,
        voice: undefined,
        file: undefined,
      };
    } else {
      throw error;
    }
  }

  const links = isHtmlContent(decryptedContent)
    ? extractLinksFromHtmlWithTiptap(decryptedContent)
    : extractLinksFromText(decryptedContent);

  // TO DO => temporary fix for backwards compatibility with previous app
  const cleanText = decryptedContent === "<p></p>" ? "" : decryptedContent;

  return {
    text: cleanText,
    link: links[0],
    ...decryptedAttachmentMeta,
    ...(attachmentMetaFailed ? { attachmentDecryptionFailed: true } : {}),
  };
};

export const decryptMessageAttachment = async (
  csk: string,
  encryptedAttachment: Blob,
  mimeType: string,
): Promise<Blob | null> => {
  if (!csk) {
    return null;
  }

  if (!encryptedAttachment) {
    throw new Error("Encrypted attachment not provided");
  }

  try {
    const uint8ArrayAttachment = await blobToUint8Array(encryptedAttachment);

    const decryptedUint8ArrayAttachment = await decryptAsset(
      uint8ArrayAttachment,
      csk,
    );

    const result = new Blob([decryptedUint8ArrayAttachment], {
      type: normalizeMimeType(mimeType),
    });

    // A failed decrypt is laundered into an EMPTY buffer, not an error:
    // windowCryptoDecrypt catches everything (including OOM on a large file)
    // and returns undefined, and decryptAsset turns that into
    // `new Uint8Array()`. Wrapping it produces a 0-byte Blob with a perfectly
    // valid MIME type, which then passes every downstream guard — the liveness
    // check succeeds because slicing 0 bytes works, so it gets cached
    // permanently and saved to disk as an empty file while the UI reports
    // success. Treat empty output as the failure it is.
    //
    // This tests the DECRYPTED size, so a genuinely empty source file lands
    // here too — it encrypts to a valid 28-byte object and decrypts back to
    // zero, indistinguishable from a failure at this point. That case is
    // rejected at staging instead (`attachmentSizeRejection`), which is the
    // only place the difference is still visible.
    if (result.size === 0) {
      return null;
    }

    return result;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return null;
  }
};

export const encryptMessageContent = async (
  eCSK: string,
  epoch: number,
  chatId: string,
  content: string,
  // encryptedAttachmentMeta?: string,
): Promise<{ encryptedContent: string; key_epoch: number }> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  if (!content) {
    // throw new Error("Message content not provided");
    return { encryptedContent: "", key_epoch: epoch };
  }

  if (!eCSK && epoch === 0) {
    throw new Error("Encrypted chat secret not provided");
  }

  try {
    const csk = await resolveCSK(
      chatId,
      epoch,
      epoch === 0 ? eCSK || undefined : undefined,
    );

    const encryptedContent = await encryptString(content, csk);

    return { encryptedContent, key_epoch: epoch };
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot encrypt content");
  }
};

/**
 * Encrypts attachment metadata using the chat secret key
 *
 * @param eCSK Encrypted chat secret key
 * @param attachmentMeta Metadata for the attachment
 * @returns Encrypted attachment metadata as a string
 */
export const encryptMessageAttachmentMeta = async (
  eCSK: string,
  epoch: number,
  chatId: string,
  attachmentMeta: AttachmentMeta,
): Promise<string> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  if (!attachmentMeta) {
    throw new Error("Attachment metadata not provided");
  }

  if (!eCSK && epoch === 0) {
    throw new Error("Encrypted chat secret not provided");
  }

  try {
    const csk = await resolveCSK(
      chatId,
      epoch,
      epoch === 0 ? eCSK || undefined : undefined,
    );

    // Convert attachment metadata to JSON string and encrypt
    const metaString = JSON.stringify(attachmentMeta);
    const encryptedMeta = await encryptString(metaString, csk);

    return encryptedMeta;
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot encrypt attachment metadata");
  }
};

export const decryptMessageAttachmentMeta = async (
  attachmentMeta: string,
  csk: string,
): Promise<Omit<MessageContentEntity, "text" | "link">> => {
  if (!attachmentMeta) {
    return {
      media: undefined,
      voice: undefined,
      file: undefined,
    };
  }

  return await withDecryptionErrorHandling(
    "ATTACHMENT_META_DECRYPT_FAILED",
    async () => {
      const decryptedAttachmentMeta = await decryptString(attachmentMeta, csk);
      const parsedAttachmentMeta: AttachmentMeta | VoiceAttachmentMeta =
        JSON.parse(decryptedAttachmentMeta);

      return {
        media:
          getAttachmentCategory(parsedAttachmentMeta) === "media"
            ? (parsedAttachmentMeta as AttachmentMeta)
            : undefined,
        voice:
          getAttachmentCategory(parsedAttachmentMeta) === "voice"
            ? (parsedAttachmentMeta as VoiceAttachmentMeta)
            : undefined,
        file:
          getAttachmentCategory(parsedAttachmentMeta) === "file"
            ? (parsedAttachmentMeta as AttachmentMeta)
            : undefined,
      };
    },
  );
};

export const encryptMessageAttachment = async (
  eCSK: string,
  epoch: number,
  chatId: string,
  attachment: File,
  // encryptedAttachmentMeta?: string,
): Promise<Blob> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  if (!attachment) {
    throw new Error("Encrypted attachment not provided");
  }

  if (!eCSK && epoch === 0) {
    throw new Error("Encrypted chat secret not provided");
  }

  try {
    const csk = await resolveCSK(
      chatId,
      epoch,
      epoch === 0 ? eCSK || undefined : undefined,
    );

    const encryptedAttachment = await encryptAsset(attachment, csk);

    return encryptedAttachment;
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot encrypt attachment");
  }
};

/*
 * Handle SERVICE messages
 * They are not encrypted, only base64 encoded
 */

/**
 * Shared decode for both service-message decryptors (they were two
 * byte-identical copies that could drift — the backend explicitly warned
 * "whatever you touch, touch both", so now there is only one).
 *
 * The mapping is a LOCKED CONTRACT with the backend (account deletion, BE
 * handoff §5.1). Only two service tags exist: `UserAdded` and `UserDeleted`.
 *
 * - `UserAdded`               → "{username} was added to the chat"
 * - `UserDeleted`, sender === content.username
 *                             → "{username} left the chat" (leftChat).
 *   This covers BOTH a voluntary leave AND account deletion: the deletion
 *   notice deliberately sets sender === content.username, and the backend
 *   EXEMPTS service messages from sender anonymisation specifically so this
 *   equality keeps working. Do NOT "fix" this branch to key on the tag —
 *   that reverts a deliberate cross-team decision (see
 *   docs/account-deletion.md).
 * - `UserDeleted`, sender !== content.username (an admin removed them)
 *                             → "{username} was removed from the chat"
 *
 * `content.username` always carries the ORIGINAL username (it survives
 * anonymisation because the payload is opaque to the server).
 */
const decodeServiceMessageContent = (message: {
  content: string;
  sender: string;
}): MessageContentEntity => {
  const content = JSON.parse(window.atob(message.content));
  const tag = content.tag;
  const isUserAdded = tag === "UserAdded";
  const isSender = message.sender === content.username;
  if (isUserAdded) {
    return { text: i18n.t(tag, { username: content.username }) };
  }
  if (isSender) {
    return { text: i18n.t("leftChat", { username: message.sender }) };
  }
  return { text: i18n.t(tag, { username: content.username }) };
};

export const decryptServiceMessageContent = async (
  message: EncryptedMessage,
): Promise<MessageContentEntity> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    logDecryptionFailure("MISSING_AUTH");
    throw new DecryptionError("MISSING_AUTH");
  }

  return await withDecryptionErrorHandling("CIPHERTEXT_DECRYPT_FAILED", () =>
    Promise.resolve(decodeServiceMessageContent(message)),
  );
};

// TO DO => Temporary function, to be removed when umbra messages are fully integrated
export const umbra_decryptServiceMessageContent = async (
  message: UmbraEncryptedMessage,
): Promise<MessageContentEntity> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    logDecryptionFailure("MISSING_AUTH");
    throw new DecryptionError("MISSING_AUTH");
  }

  return await withDecryptionErrorHandling("CIPHERTEXT_DECRYPT_FAILED", () =>
    Promise.resolve(decodeServiceMessageContent(message)),
  );
};

export async function* encryptFileAsChunks(
  file: File,
  csk: string,
  chunkSize = 5 * 1024 * 1024,
): AsyncGenerator<Uint8Array> {
  const keyBytes = hexToUint8Array(csk);
  let offset = 0;
  while (offset < file.size) {
    const slice = file.slice(offset, offset + chunkSize);
    const buffer = await slice.arrayBuffer();
    yield await encryptChunk(new Uint8Array(buffer), keyBytes);
    offset += chunkSize;
  }
}

/**
 * Encrypts a single plaintext chunk of `file` by 0-based index. Chunks are
 * independent AES-GCM messages, so callers may encrypt and upload them in any
 * order — used by the moov-first chunked upload ordering, where the header
 * part and the moov-bearing tail parts go up before the middle of the file.
 */
export async function encryptFileChunk(
  file: File,
  csk: string,
  chunkIndex: number,
  chunkSize = 5 * 1024 * 1024,
): Promise<Uint8Array> {
  const keyBytes = hexToUint8Array(csk);
  const offset = chunkIndex * chunkSize;
  const buffer = await file.slice(offset, offset + chunkSize).arrayBuffer();
  return encryptChunk(new Uint8Array(buffer), keyBytes);
}
